import { avatarDefault } from '@/assets/images';
import { Divider, Stack } from '@/components/layout';
import { Level } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { PlayerCardAvatar } from '@/features/player/components/list-player/player-card/player-card-avatar';
import { PlayerCardDesc } from '@/features/player/components/list-player/player-card/player-card-desc';
import {
  PlayerCardIndicator,
  PlayerCardRoot,
} from '@/features/player/components/list-player/player-card/player-card-root';
import { PlayerCardTitle } from '@/features/player/components/list-player/player-card/player-card-title';
import { getAvatarCacheBuster } from '@/features/player/utils/avatar-cache-buster';
import { buildPlayerAvatarSource } from '@/features/player/utils/avatar-uri';
import { useScaling } from '@/hooks';
import { PlayerStackParamList } from '@/navigations/types';
import { colors, ColorToken } from '@/theme/colors';
import { theme } from '@/theme/theme';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { memo } from 'react';
import { Platform, StyleSheet } from 'react-native';

export const PlayerCard = memo(
  ({ player }: { player: player.PlayerLookupData }) => {
    const { scale, isTablet } = useScaling();
    const navigation = useNavigation<NativeStackNavigationProp<PlayerStackParamList>>();
    const avatarSize = scale(48);

    const avatarSource =
      buildPlayerAvatarSource({
        playerId: player.id,
        avatarUrl: player.avatarUrl ?? player.profilePicture,
      }) ?? avatarDefault;

    const onPress = () => {
      navigation.navigate('PlayerProfile', { playerId: player.id });
    };

    return (
      <Stack paddingX={scale({ phone: 18, tablet: 0 })}>
        <Stack style={styles.playerCardShadow}>
          <PlayerCardRoot marginX={0} hideShadow onPress={onPress}>
            <PlayerCardIndicator
              marginRight={scale(7)}
              color={colors[`Level ${player.level}` as ColorToken]}
            />
            <PlayerCardAvatar size={avatarSize} source={avatarSource} onPress={onPress} />

            <Divider
              orientation="vertical"
              thickness={isTablet ? 1 : StyleSheet.hairlineWidth}
              color={colors['6']}
              spacing={scale(0)}
            />

            <Stack flex={1} justify="center" gap={scale(8)}>
              <Stack gap={scale(2)}>
                <PlayerCardTitle
                  title={player.name.replace(STRINGS.player.listPlayers.creatingPlayerMarker, '')}
                />
                <PlayerCardDesc>
                  {`${player.age ?? ''}`}{' '}
                  {player.age !== null && player.age !== undefined
                    ? player.age > 1
                      ? STRINGS.player.listPlayers.ageYearsOld
                      : STRINGS.player.listPlayers.ageYearOld
                    : ''}
                </PlayerCardDesc>
              </Stack>

              <Level value={player.level} alignSelf="flex-start" />
            </Stack>
          </PlayerCardRoot>
        </Stack>
      </Stack>
    );
  },
  (prev, next) => {
    const prevV = getAvatarCacheBuster(prev.player.id);
    const nextV = getAvatarCacheBuster(next.player.id);
    return (
      prev.player.id === next.player.id &&
      prev.player.name === next.player.name &&
      prev.player.age === next.player.age &&
      prev.player.level === next.player.level &&
      prev.player.avatarUrl === next.player.avatarUrl &&
      prev.player.profilePicture === next.player.profilePicture &&
      prevV === nextV
    );
  },
);

const styles = StyleSheet.create({
  playerCardShadow: {
    backgroundColor: theme.colors['Backgrounds/Primary'],
    borderRadius: 4,
    overflow: 'hidden',
    ...(Platform.OS === 'ios'
      ? {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.16,
          shadowRadius: 2,
        }
      : {
          elevation: 2,
        }),
  },
});
